// ─── src/ElevateApplicationsView.tsx ─────────────────────────────────────────
//
// Startup Syria Elevate · Damascus 2026 — mentor applications view.
// Follows the same shape as the NPD registrations view: stat row,
// filters, paginated table, detail drawer with review editor.

import { useEffect, useState } from "react";
import {
  Download,
  Eye,
  MapPin,
  Plus,
  Rocket,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import {
  useDeleteElevateApplication,
  useElevateApplications,
  useElevateStats,
  useReviewElevateApplication,
} from "./hooks/useElevate";
import { Pagination } from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard } from "./components/ui/StatCard";
import type {
  ElevateAdminStatus,
  ElevateApplication,
} from "./api/elevate";

const LIMIT = 20;

const ADMIN_STATUS_LABEL: Record<ElevateAdminStatus, string> = {
  new_applicant: "New applicant",
  shortlisted: "Shortlisted",
  interview: "Interview",
  accepted: "Accepted",
  rejected: "Rejected",
};
const ADMIN_STATUS_ORDER: ElevateAdminStatus[] = [
  "new_applicant",
  "shortlisted",
  "interview",
  "accepted",
  "rejected",
];
const ADMIN_STATUS_CLASS: Record<ElevateAdminStatus, string> = {
  new_applicant: "bg-slate-500/15 text-slate-200 border border-slate-500/40",
  shortlisted: "bg-amber-500/15 text-amber-300 border border-amber-500/40",
  interview: "bg-blue-500/15 text-blue-300 border border-blue-500/40",
  accepted: "bg-brand-green/15 text-brand-green border border-brand-green/40",
  rejected: "bg-red-500/15 text-red-300 border border-red-500/40",
};

const GENDER_LABEL: Record<string, string> = {
  male: "Male",
  female: "Female",
  prefer_not_to_say: "Prefer not to say",
};
const AGE_LABEL: Record<string, string> = {
  under_18: "Under 18",
  "19_25": "19–25",
  "26_35": "26–35",
  "36_50": "36–50",
  over_50: "50+",
};
const STATUS_LABEL: Record<string, string> = {
  student: "Student",
  graduate: "Graduate",
  employee: "Employee",
  startup_founder: "Startup Founder",
  freelancer: "Freelancer",
};
const SKILL_LABEL: Record<string, string> = {
  entrepreneurship: "Entrepreneurship",
  expert: "Expert",
  business: "Business",
  designer: "Designer",
  developer: "Developer",
};
const GOV_LABEL: Record<string, string> = {
  damascus: "Damascus",
  rural_damascus: "Rural Damascus",
  aleppo: "Aleppo",
  homs: "Homs",
  hama: "Hama",
  latakia: "Latakia",
  tartus: "Tartous",
  daraa: "Daraa",
  sweida: "As-Suwayda",
  deir_ezzor: "Deir ez-Zor",
  hasakah: "Al-Hasakah",
  raqqa: "Raqqa",
  quneitra: "Quneitra",
  idlib: "Idlib",
};
const YEARS_LABEL: Record<string, string> = {
  under_5: "Fewer than 5",
  "5_9": "5 – 9",
  "10_14": "10 – 14",
  "15_19": "15 – 19",
  over_20: "20+",
};
const EXPERTISE_LABEL: Record<string, string> = {
  strategy_growth: "Strategy & growth",
  entrepreneurship_building: "Entrepreneurship & company building",
  finance_management: "Finance & financial management",
  investment_fundraising: "Investment & fundraising",
  sales_market_access: "Sales, market access & partnerships",
  marketing_customer_insight: "Marketing & customer insight",
  operations_organisation: "Operations & org dev",
  product_technology: "Product, technology & innovation",
  governance_legal_risk: "Governance, legal & risk",
  ecosystem_development: "Ecosystem development",
  sector_other: "Sector-specific (other)",
  other: "Other",
};
const LEARNING_LABEL: Record<string, string> = {
  understanding_ecosystem: "Understanding the startup ecosystem",
  assessing_business_models: "Assessing business models",
  startup_finance_unit_economics: "Startup finance & unit economics",
  investment_stages_decisions: "Investment stages & decisions",
  term_sheets_dilution: "Term sheets, ownership & dilution",
  investment_readiness: "Investment readiness",
  structured_mentoring: "Structured mentoring conversations",
};
const ATTENDANCE_LABEL: Record<string, string> = {
  yes: "Yes",
  need_to_confirm: "Need to confirm",
  no: "No",
};

const CSV_HEADERS = [
  "Full name",
  "Email",
  "Phone",
  "Gender",
  "Age",
  "Current status (legacy)",
  "Skills (legacy)",
  "Governorate",
  "City",
  "Years experience",
  "LinkedIn URL",
  "LinkedIn / bio (legacy)",
  "Nominated",
  "Nominating organisation",
  "Expertise areas",
  "Sector (other)",
  "Expertise (other)",
  "Motivation",
  "Learning areas",
  "Attendance",
  "Admin status",
  "Admin tags",
  "Submitted at",
];

function exportCsv(rows: ElevateApplication[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((r) =>
    [
      r.fullName,
      r.email,
      r.phone,
      GENDER_LABEL[r.gender] ?? r.gender,
      AGE_LABEL[r.ageBracket] ?? r.ageBracket,
      r.currentStatus ? (STATUS_LABEL[r.currentStatus] ?? r.currentStatus) : "",
      (r.skills ?? []).map((s) => SKILL_LABEL[s] ?? s).join(" · "),
      GOV_LABEL[r.governorate] ?? r.governorate,
      r.city,
      YEARS_LABEL[r.yearsExperience] ?? r.yearsExperience,
      r.linkedinUrl ?? "",
      r.linkedinOrBio ?? "",
      r.nominated ? "Yes" : "No",
      r.nominatingOrganisation ?? "",
      (r.expertiseAreas ?? [])
        .map((e) => EXPERTISE_LABEL[e] ?? e)
        .join(" · "),
      r.expertiseSectorOther ?? "",
      r.expertiseOther ?? "",
      r.motivation,
      (r.learningAreas ?? []).map((l) => LEARNING_LABEL[l] ?? l).join(" · "),
      ATTENDANCE_LABEL[r.attendance] ?? r.attendance,
      ADMIN_STATUS_LABEL[r.adminStatus] ?? r.adminStatus,
      (r.adminTags ?? []).join(" · "),
      r.createdAt,
    ]
      .map(esc)
      .join(","),
  );
  const blob = new Blob(["﻿" + [CSV_HEADERS.join(","), ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `elevate_applications_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function AdminStatusPill({ status }: { status: ElevateAdminStatus }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${ADMIN_STATUS_CLASS[status]}`}
    >
      {ADMIN_STATUS_LABEL[status]}
    </span>
  );
}

export default function ElevateApplicationsView() {
  const [search, setSearch] = useState("");
  const [governorate, setGovernorate] = useState("");
  const [attendance, setAttendance] = useState("");
  const [adminStatus, setAdminStatus] = useState<ElevateAdminStatus | "">("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<ElevateApplication | null>(null);

  const { data: stats } = useElevateStats();
  const { data: allData } = useElevateApplications({ page: 1, limit: 2000 });
  const {
    data: tableData,
    isLoading,
    isFetching,
    isError,
  } = useElevateApplications({
    page,
    limit: LIMIT,
    search,
    governorate,
    attendance,
    adminStatus: adminStatus || undefined,
  });

  const { mutate: deleteRow, isPending: isDeleting } =
    useDeleteElevateApplication();

  useEffect(() => {
    if (!detail) return;
    const latest = tableData?.data.find((r) => r.id === detail.id);
    if (latest && latest.updatedAt !== detail.updatedAt) setDetail(latest);
  }, [tableData, detail]);

  const rows = tableData?.data ?? [];
  const meta =
    tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };

  const topGov = Object.entries(stats?.byGovernorate ?? {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);
  const govOptions = Object.keys(stats?.byGovernorate ?? {}).sort();
  const attendanceOptions = Object.keys(stats?.byAttendance ?? {});

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard
          label="Applications"
          value={stats?.total ?? 0}
          icon={Rocket}
          accent="bg-brand-orange"
        />
        <StatCard
          label="New"
          value={stats?.byAdminStatus?.new_applicant ?? 0}
          icon={Rocket}
          accent="bg-slate-500"
        />
        <StatCard
          label="Shortlisted"
          value={stats?.byAdminStatus?.shortlisted ?? 0}
          icon={Rocket}
          accent="bg-amber-500"
        />
        <StatCard
          label="Interview"
          value={stats?.byAdminStatus?.interview ?? 0}
          icon={Rocket}
          accent="bg-blue-500"
        />
        <StatCard
          label="Accepted"
          value={stats?.byAdminStatus?.accepted ?? 0}
          icon={Rocket}
          accent="bg-brand-green"
        />
      </div>

      {topGov.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs text-brand-muted">
          <span className="font-mono uppercase tracking-widest">
            Top governorates:
          </span>
          {topGov.map(([g, count]) => (
            <span
              key={g}
              className="inline-flex items-center gap-1 rounded-full border border-brand-border bg-brand-navy px-2 py-0.5"
            >
              <MapPin size={10} />
              {GOV_LABEL[g] ?? g}: {count}
            </span>
          ))}
        </div>
      )}

      <div className="card p-4 flex flex-col lg:flex-row items-start lg:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search name, email, city…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={governorate}
          onChange={(e) => {
            setGovernorate(e.target.value);
            setPage(1);
          }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All governorates</option>
          {govOptions.map((g) => (
            <option key={g} value={g}>
              {GOV_LABEL[g] ?? g}
            </option>
          ))}
        </select>
        <select
          value={attendance}
          onChange={(e) => {
            setAttendance(e.target.value);
            setPage(1);
          }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All attendance</option>
          {attendanceOptions.map((a) => (
            <option key={a} value={a}>
              {ATTENDANCE_LABEL[a] ?? a}
            </option>
          ))}
        </select>
        <select
          value={adminStatus}
          onChange={(e) => {
            setAdminStatus(e.target.value as ElevateAdminStatus | "");
            setPage(1);
          }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All pipeline statuses</option>
          {ADMIN_STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {ADMIN_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <button
          onClick={() => exportCsv(allData?.data ?? [])}
          disabled={(allData?.data ?? []).length === 0}
          className="btn-primary text-sm flex items-center gap-1.5 ml-auto disabled:opacity-50"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">
          Failed to load applications.
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-brand-muted">
          No applications yet.
        </div>
      ) : (
        <>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-border text-left text-brand-muted">
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Applicant</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Years exp</th>
                  <th className="px-4 py-3 font-medium">Governorate</th>
                  <th className="px-4 py-3 font-medium">Attendance</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors"
                  >
                    <td className="px-4 py-3 text-brand-muted">
                      {(meta.page - 1) * LIMIT + i + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">
                        {r.fullName}
                      </div>
                      <div className="text-brand-muted text-xs">{r.email}</div>
                      {r.adminTags && r.adminTags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {r.adminTags.slice(0, 3).map((t) => (
                            <span
                              key={t}
                              className="inline-block rounded-full border border-brand-border bg-brand-navy px-1.5 py-0 text-[9px] text-brand-muted"
                            >
                              {t}
                            </span>
                          ))}
                          {r.adminTags.length > 3 && (
                            <span className="text-[9px] text-brand-muted">
                              +{r.adminTags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <AdminStatusPill status={r.adminStatus} />
                    </td>
                    <td className="px-4 py-3 text-brand-muted">
                      {YEARS_LABEL[r.yearsExperience] ?? r.yearsExperience}
                    </td>
                    <td className="px-4 py-3 text-brand-muted">
                      {GOV_LABEL[r.governorate] ?? r.governorate}
                    </td>
                    <td className="px-4 py-3 text-brand-muted">
                      {ATTENDANCE_LABEL[r.attendance] ?? r.attendance}
                    </td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3 flex items-center gap-1">
                      <button
                        onClick={() => setDetail(r)}
                        className="p-1.5 rounded-lg text-brand-muted hover:text-brand-green hover:bg-brand-green/10"
                        title="View details"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm("Delete this application?"))
                            deleteRow(r.id);
                        }}
                        disabled={isDeleting}
                        className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={isFetching}
          />
        </>
      )}

      {detail && (
        <DetailDrawer row={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}

function DetailDrawer({
  row,
  onClose,
}: {
  row: ElevateApplication;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl h-full overflow-y-auto bg-brand-navy border-l border-brand-border p-6 space-y-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-brand-muted">
              Elevate · Damascus 2026
            </p>
            <h2 className="mt-1 text-xl font-bold text-white">{row.fullName}</h2>
            <p className="text-sm text-brand-muted">{row.email}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border/40"
          >
            <X size={16} />
          </button>
        </div>

        <ReviewEditor row={row} />

        <section className="space-y-3">
          <h3 className="text-xs font-mono uppercase tracking-widest text-brand-orange">
            Professional profile
          </h3>
          <Pair label="Phone" value={row.phone} />
          <Pair label="Gender" value={GENDER_LABEL[row.gender] ?? row.gender} />
          <Pair label="Age" value={AGE_LABEL[row.ageBracket] ?? row.ageBracket} />
          {/* Legacy fields — still rendered when old applications
              carry them, hidden on 2026 submissions. */}
          {row.currentStatus && (
            <Pair
              label="Current status"
              value={STATUS_LABEL[row.currentStatus] ?? row.currentStatus}
            />
          )}
          {(row.skills ?? []).length > 0 && (
            <Pair
              label="Skills"
              value={(row.skills ?? [])
                .map((s) => SKILL_LABEL[s] ?? s)
                .join(" · ")}
            />
          )}
          <Pair
            label="Location"
            value={`${GOV_LABEL[row.governorate] ?? row.governorate} · ${row.city}`}
          />
          <Pair
            label="Years experience"
            value={YEARS_LABEL[row.yearsExperience] ?? row.yearsExperience}
          />
          {row.linkedinUrl && (
            <Pair label="LinkedIn" value={row.linkedinUrl} wrap />
          )}
          {row.linkedinOrBio && (
            <Pair label="LinkedIn / bio (legacy)" value={row.linkedinOrBio} wrap />
          )}
          <Pair label="Nominated" value={row.nominated ? "Yes" : "No"} />
          {row.nominated && (
            <Pair
              label="Nominating organisation"
              value={row.nominatingOrganisation ?? "—"}
            />
          )}
        </section>

        <section className="space-y-3">
          <h3 className="text-xs font-mono uppercase tracking-widest text-brand-orange">
            Expertise & mentoring
          </h3>
          <Pair
            label="Expertise areas"
            value={(row.expertiseAreas ?? [])
              .map((e) => EXPERTISE_LABEL[e] ?? e)
              .join(" · ")}
          />
          {row.expertiseSectorOther && (
            <Pair label="Sector (other)" value={row.expertiseSectorOther} />
          )}
          {row.expertiseOther && (
            <Pair label="Other expertise" value={row.expertiseOther} />
          )}
          <Pair
            label="Learning areas"
            value={(row.learningAreas ?? [])
              .map((l) => LEARNING_LABEL[l] ?? l)
              .join(" · ")}
          />
          <Pair label="Motivation" value={row.motivation} wrap />
          <Pair
            label="Attendance"
            value={ATTENDANCE_LABEL[row.attendance] ?? row.attendance}
          />
        </section>

        <p className="text-xs text-brand-muted">
          Submitted{" "}
          {new Date(row.createdAt).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
    </div>
  );
}

function ReviewEditor({ row }: { row: ElevateApplication }) {
  const [status, setStatus] = useState<ElevateAdminStatus>(row.adminStatus);
  const [notes, setNotes] = useState<string>(row.adminNotes ?? "");
  const [tags, setTags] = useState<string[]>(row.adminTags ?? []);
  const [tagDraft, setTagDraft] = useState<string>("");

  useEffect(() => {
    setStatus(row.adminStatus);
    setNotes(row.adminNotes ?? "");
    setTags(row.adminTags ?? []);
    setTagDraft("");
  }, [row.id, row.updatedAt, row.adminStatus, row.adminNotes, row.adminTags]);

  const review = useReviewElevateApplication();

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t || tags.includes(t) || tags.length >= 20) {
      setTagDraft("");
      return;
    }
    setTags([...tags, t]);
    setTagDraft("");
  };
  const removeTag = (t: string) => setTags(tags.filter((x) => x !== t));

  const dirty =
    status !== row.adminStatus ||
    (notes.trim() || null) !== (row.adminNotes ?? null) ||
    JSON.stringify(tags) !== JSON.stringify(row.adminTags ?? []);

  const save = () => {
    review.mutate({
      id: row.id,
      patch: {
        adminStatus: status,
        adminNotes: notes.trim() ? notes.trim() : null,
        adminTags: tags,
      },
    });
  };

  return (
    <section className="rounded-xl border border-brand-border bg-brand-navy/60 p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-mono uppercase tracking-widest text-brand-orange">
          Review
        </h3>
        <AdminStatusPill status={row.adminStatus} />
      </div>

      <div className="space-y-1.5">
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-brand-muted">
          Pipeline status
        </label>
        <div className="flex flex-wrap gap-2">
          {ADMIN_STATUS_ORDER.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                status === s
                  ? ADMIN_STATUS_CLASS[s]
                  : "border-brand-border text-brand-muted hover:text-white"
              }`}
            >
              {ADMIN_STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-brand-muted">
          Tags
        </label>
        <div className="flex flex-wrap gap-1.5">
          {tags.length === 0 && (
            <span className="text-xs text-brand-muted">No tags yet.</span>
          )}
          {tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-full border border-brand-border bg-brand-navy px-2 py-0.5 text-xs text-white"
            >
              {t}
              <button
                type="button"
                onClick={() => removeTag(t)}
                className="text-brand-muted hover:text-red-400"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <input
            type="text"
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="Add a tag…"
            maxLength={60}
            className="flex-1 px-2.5 py-1.5 text-xs bg-brand-navy border border-brand-border rounded-md text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
          />
          <button
            type="button"
            onClick={addTag}
            disabled={!tagDraft.trim()}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-brand-muted border border-brand-border rounded-md hover:text-white disabled:opacity-40"
          >
            <Plus size={12} /> Add
          </button>
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-brand-muted">
          Notes
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          maxLength={5000}
          placeholder="Internal review notes — not shown to the applicant."
          className="w-full px-3 py-2 text-sm bg-brand-navy border border-brand-border rounded-md text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
        />
      </div>

      <div className="flex items-center justify-end gap-2">
        {review.isSuccess && !dirty && (
          <span className="text-[11px] text-brand-green">Saved.</span>
        )}
        {review.isError && (
          <span className="text-[11px] text-red-400">Save failed.</span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={!dirty || review.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-green rounded-md hover:bg-brand-green/90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Save size={12} />
          {review.isPending ? "Saving…" : "Save review"}
        </button>
      </div>
    </section>
  );
}

function Pair({
  label,
  value,
  wrap,
}: {
  label: string;
  value: React.ReactNode;
  wrap?: boolean;
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
      <span className="text-brand-muted">{label}</span>
      <span
        className={`text-white ${wrap ? "whitespace-pre-wrap leading-relaxed" : ""}`}
      >
        {value || <span className="text-brand-muted">—</span>}
      </span>
    </div>
  );
}
